import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Loader2 } from "lucide-react";
import { useSite } from "@/lib/site";

export default function Login() {
  const site = useSite();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-brand p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
            <BookOpen className="h-6 w-6" />
          </span>
          <div>
            <div className="font-display text-lg font-bold">{site.name}</div>
            <div className="text-xs uppercase tracking-widest text-white/70">Company accounts</div>
          </div>
        </div>
        <div>
          <h1 className="font-display text-4xl font-bold leading-tight">
            The books,<br />straight from the station.
          </h1>
          <p className="mt-4 max-w-md text-white/80">
            Ledger, trial balance, profit &amp; loss and balance sheet - with every shift,
            delivery and stock count posted automatically.
          </p>
        </div>
        <div className="text-xs text-white/70">{site.company}</div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white">
              <BookOpen className="h-6 w-6" />
            </span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">Admin &amp; Accountant access</p>
          </div>
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required data-testid="login-email-input" autoComplete="email" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs font-medium text-[#1E3A8A] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required data-testid="login-password-input" autoComplete="current-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand/90" data-testid="login-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
